import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { RefObject } from "react";
import type { RealtimeConnection } from "@elevenlabs/client";
import { mintVoiceToken } from "../lib/voice";
import type { VoiceApiError } from "../lib/voice";

type VoiceStatus = "idle" | "connecting" | "listening" | "error";

export type VoiceInputButtonHandle = {
  stop: () => void;
  isActive: () => boolean;
};

type VoiceInputButtonProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  className?: string;
};

type ElevenLabsClient = typeof import("@elevenlabs/client");

const SVG_PROPS = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
} as const;

const MicrophoneIcon = ({ className = "size-4" }: { className?: string }) => (
  <svg {...SVG_PROPS} strokeWidth={2} className={className}>
    <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <path d="M12 19v3" />
  </svg>
);

const MicrophoneOffIcon = ({ className = "size-4" }: { className?: string }) => (
  <svg {...SVG_PROPS} strokeWidth={2} className={className}>
    <path d="M9 9v3a3 3 0 0 0 4.9 2.3" />
    <path d="M15 9.3V6a3 3 0 0 0-5.1-2.1" />
    <path d="M5 10v2a7 7 0 0 0 11.7 5.2" />
    <path d="M19 10v2a6.9 6.9 0 0 1-.7 3" />
    <path d="M12 19v3" />
    <path d="M3 3l18 18" />
  </svg>
);

const LoaderIcon = ({ className = "size-4" }: { className?: string }) => (
  <svg {...SVG_PROPS} strokeWidth={2} className={className}>
    <path d="M21 12a9 9 0 1 1-6.2-8.6" />
  </svg>
);

const VoiceInputButton = forwardRef<VoiceInputButtonHandle, VoiceInputButtonProps>(
  function VoiceInputButton(
    { value, onChange, disabled = false, inputRef, className },
    ref,
  ) {
    const [status, setStatus] = useState<VoiceStatus>("idle");
    const [errorMessage, setErrorMessage] = useState("");

    const connectionRef = useRef<RealtimeConnection | null>(null);
    const baseTextRef = useRef("");
    const committedRef = useRef("");
    const partialRef = useRef("");
    const valueRef = useRef(value);
    const onChangeRef = useRef(onChange);
    const sdkPromiseRef = useRef<Promise<ElevenLabsClient> | null>(null);

    useEffect(() => {
      valueRef.current = value;
    }, [value]);

    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    const ensureSdk = useCallback(() => {
      if (!sdkPromiseRef.current) {
        sdkPromiseRef.current = import("@elevenlabs/client").catch((error) => {
          sdkPromiseRef.current = null;
          throw error;
        });
      }
      return sdkPromiseRef.current;
    }, []);

    const pushTranscript = useCallback(() => {
      onChangeRef.current(
        joinParts(baseTextRef.current, committedRef.current, partialRef.current),
      );
    }, []);

    const closeConnection = useCallback(() => {
      const connection = connectionRef.current;
      connectionRef.current = null;
      if (connection) {
        try {
          connection.close();
        } catch {
          // Connection cleanup is best-effort; the SDK may already be closed.
        }
      }
    }, []);

    const stopListening = useCallback(
      (nextStatus: VoiceStatus = "idle", keepError = false) => {
        closeConnection();
        if (!keepError) setErrorMessage("");
        setStatus(nextStatus);
      },
      [closeConnection],
    );

    useEffect(() => () => closeConnection(), [closeConnection]);

    useImperativeHandle(
      ref,
      () => ({
        stop: () => {
          if (connectionRef.current) stopListening();
        },
        isActive: () => status === "connecting" || status === "listening",
      }),
      [status, stopListening],
    );

    useEffect(() => {
      if (disabled) return undefined;
      const warmSdk = () => {
        void ensureSdk().catch(() => undefined);
      };

      if ("requestIdleCallback" in window) {
        const idleHandle = window.requestIdleCallback(warmSdk, { timeout: 1500 });
        return () => window.cancelIdleCallback(idleHandle);
      }

      const timeout = globalThis.setTimeout(warmSdk, 500);
      return () => globalThis.clearTimeout(timeout);
    }, [disabled, ensureSdk]);

    useEffect(() => {
      const input = inputRef?.current;
      if (!input || disabled) return undefined;
      const warmSdk = () => {
        void ensureSdk().catch(() => undefined);
      };
      input.addEventListener("focus", warmSdk);
      return () => input.removeEventListener("focus", warmSdk);
    }, [disabled, ensureSdk, inputRef]);

    useEffect(() => {
      if (status === "idle" && !connectionRef.current) {
        baseTextRef.current = value ?? "";
        committedRef.current = "";
        partialRef.current = "";
      }
    }, [status, value]);

    const startListening = useCallback(async () => {
      if (status === "connecting" || status === "listening") return;

      setErrorMessage("");
      setStatus("connecting");
      baseTextRef.current = valueRef.current ?? "";
      committedRef.current = "";
      partialRef.current = "";

      if (!navigator.mediaDevices?.getUserMedia) {
        setErrorMessage("This browser does not support microphone capture.");
        setStatus("error");
        return;
      }

      let token: string;
      let modelId: string;
      try {
        const response = await mintVoiceToken();
        token = response.token;
        modelId = response.modelId;
      } catch (error) {
        setErrorMessage(parseApiError(error));
        setStatus("error");
        return;
      }

      let sdk: ElevenLabsClient;
      try {
        sdk = await ensureSdk();
      } catch {
        setErrorMessage("Failed to load speech client.");
        setStatus("error");
        return;
      }

      const onMicRejection = (event: PromiseRejectionEvent) => {
        const reason = event.reason;
        const name = errorName(reason);
        const message = errorMessageText(reason);
        if (
          name === "NotAllowedError" ||
          name === "NotFoundError" ||
          name === "NotReadableError" ||
          name === "SecurityError" ||
          /microphone|getusermedia|audio/i.test(message)
        ) {
          event.preventDefault();
          window.removeEventListener("unhandledrejection", onMicRejection);
          setErrorMessage(prettyMicError(reason));
          setStatus("error");
          closeConnection();
        }
      };
      window.addEventListener("unhandledrejection", onMicRejection);
      const clearMicRejectionListener = () => {
        window.removeEventListener("unhandledrejection", onMicRejection);
      };

      let connection: RealtimeConnection;
      try {
        connection = sdk.Scribe.connect({
          token,
          modelId,
          commitStrategy: sdk.CommitStrategy.VAD,
          vadSilenceThresholdSecs: 1.2,
          microphone: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
      } catch (error) {
        clearMicRejectionListener();
        setErrorMessage(prettyMicError(error));
        setStatus("error");
        return;
      }

      connectionRef.current = connection;

      connection.on(sdk.RealtimeEvents.OPEN, () => {
        if (connectionRef.current !== connection) return;
        clearMicRejectionListener();
        setStatus("listening");
        inputRef?.current?.focus();
      });

      connection.on(sdk.RealtimeEvents.PARTIAL_TRANSCRIPT, (data) => {
        if (connectionRef.current !== connection) return;
        partialRef.current = transcriptText(data);
        pushTranscript();
      });

      connection.on(sdk.RealtimeEvents.COMMITTED_TRANSCRIPT, (data) => {
        if (connectionRef.current !== connection) return;
        const text = transcriptText(data);
        if (text) {
          committedRef.current = appendSegment(committedRef.current, text);
        }
        partialRef.current = "";
        pushTranscript();
      });

      connection.on(sdk.RealtimeEvents.CLOSE, () => {
        clearMicRejectionListener();
        if (connectionRef.current !== connection) return;
        connectionRef.current = null;
        setStatus((current) => (current === "error" ? current : "idle"));
      });

      const onRealtimeError = (payload: unknown) => {
        clearMicRejectionListener();
        if (connectionRef.current !== connection) return;
        setErrorMessage(realtimeErrorMessage(payload));
        setStatus("error");
        stopListening("error", true);
      };

      connection.on(sdk.RealtimeEvents.ERROR, onRealtimeError);
      connection.on(sdk.RealtimeEvents.AUTH_ERROR, () =>
        onRealtimeError({ message: "ElevenLabs auth failed." }),
      );
      connection.on(sdk.RealtimeEvents.QUOTA_EXCEEDED, () =>
        onRealtimeError({ message: "ElevenLabs quota exceeded." }),
      );
      connection.on(sdk.RealtimeEvents.RATE_LIMITED, () =>
        onRealtimeError({ message: "Rate limited. Try again in a moment." }),
      );
    }, [closeConnection, ensureSdk, inputRef, pushTranscript, status, stopListening]);

    const onClick = useCallback(() => {
      if (disabled) return;
      if (status === "connecting" || status === "listening") {
        stopListening();
      } else {
        void startListening();
      }
    }, [disabled, startListening, status, stopListening]);

    const listening = status === "listening";
    const connecting = status === "connecting";
    const errored = status === "error";
    const title = listening
      ? "Stop dictation"
      : connecting
        ? "Connecting..."
        : errored
          ? errorMessage || "Voice error. Tap to retry."
          : "Start voice dictation";

    return (
      <div className={`relative flex shrink-0 items-center ${className ?? ""}`}>
        {(connecting || listening || errored) && (
          <div
            role="status"
            className={
              "pointer-events-none absolute right-0 bottom-full mb-1.5 max-w-60 truncate rounded-lg px-2 py-1 text-[0.625rem] font-medium whitespace-nowrap shadow-lg " +
              (errored
                ? "bg-red-500 text-white"
                : listening
                  ? "bg-[var(--color-brand)] text-white"
                  : "border border-neutral-200 bg-white text-neutral-700")
            }
          >
            {connecting && "Connecting..."}
            {listening && (
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="voice-blink block size-1.5 rounded-full bg-white"
                />
                Listening
              </span>
            )}
            {errored && (errorMessage || "Voice error. Tap to retry.")}
          </div>
        )}

        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          aria-label={title}
          aria-pressed={listening}
          title={title}
          className={
            "relative grid size-8 place-items-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-40 " +
            (listening
              ? "bg-[var(--color-brand)] text-white hover:bg-[var(--color-brand-dark)]"
              : errored
                ? "bg-red-50 text-red-600 hover:bg-red-100"
                : "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700")
          }
        >
          {connecting ? (
            <LoaderIcon className="size-4 animate-spin" />
          ) : errored ? (
            <MicrophoneOffIcon className="size-4" />
          ) : (
            <MicrophoneIcon className="size-4" />
          )}
          {listening && (
            <span
              aria-hidden="true"
              className="voice-pulse-ring pointer-events-none absolute inset-0 rounded-full"
            />
          )}
        </button>
      </div>
    );
  },
);

export default VoiceInputButton;

function joinParts(base: string, committed: string, partial: string): string {
  let out = base || "";
  if (committed) out = appendSegment(out, committed);
  if (partial) out = appendSegment(out, partial);
  return out;
}

function appendSegment(existing: string, segment: string): string {
  const left = existing || "";
  const right = segment || "";
  if (!left) return right;
  if (!right) return left;
  const lastChar = left.slice(-1);
  const firstChar = right.charAt(0);
  if (/\s/.test(lastChar) || /\s/.test(firstChar)) return left + right;
  return `${left} ${right}`;
}

function transcriptText(payload: unknown): string {
  if (!payload || typeof payload !== "object") return "";
  const maybeText = (payload as { text?: unknown }).text;
  if (typeof maybeText === "string") return maybeText;
  const maybeTranscript = (payload as { transcript?: unknown }).transcript;
  return typeof maybeTranscript === "string" ? maybeTranscript : "";
}

function realtimeErrorMessage(payload: unknown): string {
  if (!payload || typeof payload !== "object") return "Voice error.";
  const maybeError = (payload as { error?: unknown }).error;
  if (typeof maybeError === "string" && maybeError) return maybeError;
  const maybeMessage = (payload as { message?: unknown }).message;
  if (typeof maybeMessage === "string" && maybeMessage) return maybeMessage;
  return "Voice error.";
}

function parseApiError(error: unknown): string {
  const err = error as Partial<VoiceApiError> | null;
  const code = typeof err?.code === "string" ? err.code : "";
  const message = typeof err?.message === "string" ? err.message : "";
  if (code === "missing_api_key" || /ELEVENLABS_API_KEY|not set/i.test(message)) {
    return "Set ELEVENLABS_API_KEY on the server.";
  }
  if (code === "invalid_key" || /rejected/i.test(message)) {
    return "ElevenLabs rejected the API key.";
  }
  if (
    code === "upstream_timeout" ||
    code === "upstream_unreachable" ||
    /timeout|unreachable/i.test(message)
  ) {
    return "Could not reach ElevenLabs. Check your connection.";
  }
  return message || "Voice token request failed.";
}

function prettyMicError(error: unknown): string {
  const name = errorName(error);
  const message = errorMessageText(error);
  if (name === "NotAllowedError" || /denied|permission/i.test(message)) {
    return "Microphone permission denied.";
  }
  if (name === "NotFoundError") return "No microphone found.";
  if (name === "NotReadableError") return "Microphone is in use by another app.";
  if (name === "SecurityError") return "Microphone requires HTTPS or localhost.";
  return message || "Could not start microphone.";
}

function errorName(error: unknown): string {
  if (error && typeof error === "object" && "name" in error) {
    const name = (error as { name?: unknown }).name;
    return typeof name === "string" ? name : "";
  }
  return "";
}

function errorMessageText(error: unknown): string {
  return error instanceof Error ? error.message : String(error ?? "");
}
